import React, { useState } from "react";
import {
  Box,
  VStack,
  Input,
  Button,
  Heading,
  Text,
  Link,
  useToast,
  FormControl,
  FormLabel,
  Container,
  useColorModeValue,
  Divider,
  Flex,
  Select,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useAuth } from "../../context/AuthProvider";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { getDoc, doc } from "firebase/firestore";
import { firestore } from "../../firebase/firebaseConfig";
import { fetchUser, fetchUserProfile } from "../../services/userService";
import { fetchKYCStatus } from "../../services/kycService";
import { PeerxLogo } from "../../components/icons/Icons";
import { useTranslation } from "react-i18next";


const MotionButton = motion(Button);

const SignInPage = () => {
  const { t, i18n } = useTranslation();

  const { loginUser, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();


  // Theme Colors
  const textColor = useColorModeValue("brand.400", "white");
  const bgColor = useColorModeValue("brand.300", "gray.900");
  const cardBgColor = useColorModeValue("brand.100", "gray.700");

    // Handle language change
    const handleLanguageChange = (event) => {
      i18n.changeLanguage(event.target.value);
    };

  const handleSignIn = async () => {
    if (!email || !password) {
      toast({
        title: t("signinpage.missing_fields_title"),
        description: t("signinpage.missing_fields_message"),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);

      // Step 1: Log in the user
      const user = await loginUser(email, password);

      if (!user?.uid) {
        throw new Error(t("signinpage.invalid_user_error"));
      }

      // Step 2: Fetch user profile from Firestore
      const userProfileData = await fetchUserProfile(user.uid);

      // Step 3: Handle missing profile
      if (!userProfileData) {
        console.warn(`[SignIn] No profile data found for user: ${user.uid}`);
        toast({
          title: t("signinpage.incomplete_profile_title"),
          description: t("signinpage.incomplete_profile_message"),
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        navigate("/auth/info-details");
        return;
      }

      // Step 4: Fetch additional user data (optional)
      const userData = await fetchUser(user.uid);

      // Step 5: Redirect based on verification status
      if (!userData.emailVerified || !userData.phoneVerified) {
        navigate("/auth/verify");
        return;
      }

      // Step 6: Check KYC status
      const kycStatus = await fetchKYCStatus(user.uid);
      if (!kycStatus) {
        console.log("kys status does not exist")
        navigate("/personal/doc-info");
        return;
      }

      if (kycStatus !== "approved" && kycStatus !== "pending") {
        toast({
          title: t("signinpage.kyc_pending_title"),
          description: t("signinpage.kyc_pending_message"),
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        console.log("kyc status display:", kycStatus);
        navigate("/personal/doc-info");
        return;
      }

      // Step 7: Redirect to dashboard if all checks pass
      toast({
        title: t("signinpage.login_success_title"),
        description: t("signinpage.login_success_message"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      navigate("/personal/dashboard");
    } catch (error) {
      console.error("[SignIn] Error during login flow:", error);
      toast({
        title: t("signinpage.login_failed_title"),
        description: error.message || t("signinpage.login_failed_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);

      // Step 1: Log in with Google
      const user = await loginWithGoogle();

      if (!user?.uid) {
        throw new Error(t("signinpage.invalid_user_error"));
      }

      // Step 2: Fetch user profile from Firestore
      const userProfileData = await fetchUserProfile(user.uid);

      // Step 3: Handle missing profile
      if (!userProfileData) {
        console.warn(`[GoogleSignIn] No profile data found for user: ${user.uid}`);
        toast({
          title: t("signinpage.incomplete_profile_title"),
          description: t("signinpage.incomplete_profile_message"),
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        navigate("/auth/info-details");
        return;
      }

      // Step 4: Fetch additional user data
      const userData = await fetchUser(user.uid);

      // Step 5: Redirect based on verification status
      if (!userData.emailVerified || !userData.phoneVerified) {
        navigate("/auth/verify");
        return;
      }

      // Step 6: Check KYC status
      const kycStatus = await fetchKYCStatus(user.uid);
      if (!kycStatus) {
        navigate("/personal/doc-info");
        return;
      }

      if (kycStatus !== "approved" && kycStatus !== "pending") {
        toast({
          title: t("signinpage.kyc_pending_title"),
          description: t("signinpage.kyc_pending_message"),
          status: "info",
          duration: 3000,
          isClosable: true,
        });
        navigate("/personal/doc-info");
        return;
      }

      // Step 7: Redirect to dashboard if all checks pass
      toast({
        title: t("signinpage.google_signin_success_title"),
        description: t("signinpage.google_signin_success_message"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      navigate("/personal/dashboard");
    } catch (error) {
      console.error("[GoogleSignIn] Error during Google sign-in flow:", error);
      toast({
        title: t("signinpage.google_signin_failed_title"),
        description: error.message || t("signinpage.google_signin_failed_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box py={10} bg={bgColor} minH="100vh" pt={100}>
            {/* Language Switcher */}
            <Box position="fixed" top="4" right="4" zIndex="120" p={2}>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          width="120px"
          bg="white"
          color="black"
          size="sm"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </Select>
      </Box>
      <Container maxW="md">
        {/* Logo */}
        <Heading mb="20px" size="2xl" fontFamily="Poppins" color="brand.200" fontWeight="bold" letterSpacing="wider" as={RouterLink} to="/">
          <PeerxLogo h="90px" w="160px" />
        </Heading>
        <Box
          as={motion.div}
          bg={cardBgColor}
          borderRadius="lg"
          shadow="lg"
          mt="20px"
          p={8}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <VStack spacing={6} align="stretch">
            <Box textAlign="center" mb={4}>
              <Heading size="lg" textStyle="mainTitle" color="brand.300">
              {t("signinpage.signin_title")}
              </Heading>
            </Box>

            <FormControl isRequired>
            <FormLabel color={textColor}>{t("signinpage.email_label")}</FormLabel>
            <Input
                name="email"
                type="email"
                bg="white"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("signinpage.email_placeholder")}
              />
            </FormControl>

            <FormControl isRequired>
            <FormLabel color={textColor}>{t("signinpage.password_label")}</FormLabel>
            <Input
                name="password"
                type="password"
                bg="white"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("signinpage.password_placeholder")}
              />
            </FormControl>

            <Flex justify="flex-end">
              <Link as={RouterLink} to="/auth/forgot-password" fontSize="sm" color="blue.500">
              {t("signinpage.forgot_password_link")}
              </Link>
            </Flex>
          </VStack>
        </Box>

        <VStack spacing={4} mt={6}>
          <MotionButton
            variant="solid"
            w="full"
            onClick={handleSignIn}
            isLoading={loading}
            loadingText={t("signinpage.logging_in")}
            disabled={loading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t("signinpage.login_button")}
            </MotionButton>

          <MotionButton
            variant="outline"
            w="full"
            onClick={handleGoogleSignIn}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t("signinpage.google_signin_button")}
            </MotionButton>
        </VStack>

        <Divider my={6} />

        <Text fontSize="sm" color="gray.500" textAlign="center">
        {t("signinpage.no_account_message")}{" "}
        <Link as={RouterLink} to="/auth/sign-up" color="brand.200" fontWeight="bold">
        {t("signinpage.signup_link")}
        </Link>
        </Text>
      </Container>
    </Box>
  );
};

export default SignInPage;
